import { useContext } from 'react';
import Logger from '@lmig/dotcom-utilities-helpers/logger';
import { ContextApp } from '@lmig/dotcom-aspect-components/ContextApp';

const logger = new Logger('client:hooks:useContactPhone');

/**
 * Entities can be stored in the application's context to be used persistently across the site.
 * And example being a SalesRep's contact phone should always be placed in the footer of the
 * website when their page has been viewed. Those phone numbers are retrieved with this hook.
 *
 * @example
 * import useContactPhone from '../hooks/useContactPhone';
 *
 * const { getContactPhone } = useContactPhone();
 * const contactNumber = getContactPhone('555-555-1212');
 */

const useContactPhone = () => {
  const contextValues = useContext(ContextApp);

  if (!contextValues) {
    throw new Error('useContactPhone needs to be within a <ContextAppProvider>');
  }

  const {
    defaultContactPhone,
    phones: { drc },
  } = contextValues;

  return {
    /**
     * Determines if a contact phone number needs to be replaced/overwritten by any
     * persistent entity in session
     *
     * @param {string} defaultPhone Default phone number if no overriding numbers are available
     * @returns {string}
     * @example
     * import useContactPhone from '../hooks/useContactPhone';
     *
     * const { getContactPhone } = useContactPhone();
     * <Link href={`tel: ${getContactPhone('555-555-1212')}`} />
     */
    getContactPhone: (defaultPhone = null) => {
      if (drc) {
        logger.debug(`Returning context drc phone number ${drc.number}`);

        return drc.number;
      }

      if (defaultContactPhone) {
        logger.debug(`Returning page-level contact phone number ${defaultContactPhone}`);

        return defaultContactPhone;
      }

      logger.debug('getContactPhone(): No entity contact phone found.');

      return defaultPhone;
    },
  };
};

export default useContactPhone;
